import React, { useEffect, useRef, useState } from "react";
import {
  PACKAGE_SELECTOR_DEBOUNCE_MS,
  PACKAGE_SELECTOR_MIN_QUERY_LENGTH,
  PACKAGE_SELECTOR_RESULT_PAGE_SIZE,
} from "../constants";
import { getPackageTotalsAbortable, isCancel } from "../hooks/usePackages";

type Props = {
  facilityId: any;
  organizationId?: number,
  packageName?: string,
  onSelect?: (item: LabelMatch) => void,
  onSearchTextChange?: (text: string) => void,
  onClear?: () => void,
  mappedValue: string | undefined,
}

type LabelMatch = {
  id?: number | string;
  label?: string;
};

const DROPDOWN_HEIGHT = 220;

const LinkPackageSelector = ({ packageName = '', facilityId, organizationId, onSelect, onClear, onSearchTextChange, mappedValue = undefined }: Props) => {
  const [query, setQuery] = useState(mappedValue || '');
  const [results, setResults] = useState<LabelMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollParentRef = useRef<HTMLElement | null>(null);
  const inFlightRef = useRef<ReturnType<typeof getPackageTotalsAbortable> | null>(null);
  const debounceRef = useRef<number | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setQuery(mappedValue || '');
  }, [mappedValue]);

  const findScrollParent = (element: HTMLElement | null): HTMLElement | null => {
    let parent = element?.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  };

  const checkDropdownPosition = () => {
    if (!containerRef.current) return;

    const inputRect = containerRef.current.getBoundingClientRect();
    const scrollParent = scrollParentRef.current;

    const containerBottom = scrollParent
      ? scrollParent.getBoundingClientRect().bottom
      : window.innerHeight;

    const spaceBelow = containerBottom - inputRect.bottom;
    const spaceAbove = inputRect.top - (scrollParent
      ? scrollParent.getBoundingClientRect().top
      : 0);

    setOpenAbove(spaceBelow < DROPDOWN_HEIGHT + 10 && spaceAbove > spaceBelow);
  };

  const isDropdownVisible = loading || !!loadError || (hasSearched && !!query.trim());

  useEffect(() => {
    if (!isDropdownVisible || !containerRef.current) return;

    scrollParentRef.current = findScrollParent(containerRef.current);
    const scrollParent = scrollParentRef.current;

    checkDropdownPosition();

    if (scrollParent) {
      scrollParent.addEventListener('scroll', checkDropdownPosition);
      return () => {
        scrollParent.removeEventListener('scroll', checkDropdownPosition);
      };
    }
  }, [isDropdownVisible]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    if (mappedValue) {
      if (inFlightRef.current) inFlightRef.current.cancel();
      setResults([]);
      setLoading(false);
      setLoadError(null);
      setHasSearched(false);
      return;
    }

    if (!organizationId) {
      if (inFlightRef.current) inFlightRef.current.cancel();
      setResults([]);
      setLoading(false);
      setLoadError(null);
      setHasSearched(false);
      return;
    }

    const trimmed = query.trim();

    if (onSearchTextChange) {
      onSearchTextChange(trimmed);
    }

    if (!trimmed || trimmed.length < PACKAGE_SELECTOR_MIN_QUERY_LENGTH) {
      if (inFlightRef.current) inFlightRef.current.cancel();
      setResults([]);
      setLoading(false);
      setLoadError(null);
      setHasSearched(false);
      return;
    }

    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }

    debounceRef.current = window.setTimeout(() => {
      if (inFlightRef.current) inFlightRef.current.cancel();
      setLoading(true);
      setLoadError(null);
      setHasSearched(false);

      const request = getPackageTotalsAbortable({
        organizationId,
        label: trimmed,
        facilityId,
        page: 0,
        pageSize: PACKAGE_SELECTOR_RESULT_PAGE_SIZE,
      });
      inFlightRef.current = request;

      request.promise
        .then((resp) => {
          if (requestId !== requestIdRef.current) return;
          setResults(Array.isArray(resp?.data) ? resp.data : []);
          setHasSearched(true);
        })
        .catch((err) => {
          if (requestId !== requestIdRef.current) return;
          if (err?.name === 'AbortError' || isCancel(err)) return;
          setLoadError('Failed to load matches');
          setHasSearched(true);
        })
        .finally(() => {
          if (requestId === requestIdRef.current && inFlightRef.current === request) {
            setLoading(false);
          }
        });
    }, PACKAGE_SELECTOR_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      if (inFlightRef.current) {
        inFlightRef.current.cancel();
      }
      if (requestIdRef.current === requestId) {
        requestIdRef.current += 1;
      }
    };
  }, [facilityId, mappedValue, organizationId, query]);

  const getResultLabel = (item: LabelMatch) => {
    return String(item?.label || 'Untitled package');
  };

  const handleSelect = (item: LabelMatch) => {
    setQuery(getResultLabel(item));
    setResults([]);
    if (onSelect) {
      onSelect(item);
    }
  };

  const handleClear = () => {
    if (!onClear) return;
    if (inFlightRef.current) inFlightRef.current.cancel();
    requestIdRef.current += 1;
    setQuery('');
    setResults([]);
    setLoading(false);
    setLoadError(null);
    setHasSearched(false);
    onClear();
  };

  return (
    <div ref={containerRef} style={{ minWidth: 0, position: 'relative' }} data-testid="link-package-selector">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          className="input"
          type="text"
          placeholder="Select lab results package"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            width: '100%',
            minWidth: 0,
            cursor: !mappedValue ? 'pointer' : 'not-allowed'
          }}
          aria-label={`Map ${packageName} to existing package`}
          autoComplete="off"
          disabled={!!mappedValue}
          data-testid="link-package-selector-input"
        />
        {mappedValue && onClear && (
          <button
            type="button"
            className="button secondary"
            onClick={handleClear}
            aria-label={`Clear mapped package for ${packageName || 'package'}`}
            data-testid="link-package-selector-clear"
          >
            Clear
          </button>
        )}
      </div>

      {isDropdownVisible && (
        <div
          className="card"
          data-testid="link-package-selector-results"
          style={{
            position: 'absolute',
            ...(openAbove
              ? { bottom: '100%', marginBottom: 6 }
              : { top: '100%', marginTop: 6 }),
            left: 0,
            right: 0,
            zIndex: 20,
            padding: 6,
            maxHeight: DROPDOWN_HEIGHT,
            overflowY: 'auto'
          }}
        >
          {loading && (
            <div className="muted" style={{ fontSize: 12, padding: 6 }} data-testid="link-package-selector-loading">Searching...</div>
          )}
          {!loading && loadError && (
            <div className="muted" style={{ fontSize: 12, padding: 6 }} data-testid="link-package-selector-error">{loadError}</div>
          )}
          {!loading && !loadError && results.length === 0 && (
            <div className="muted" style={{ fontSize: 12, padding: 6 }} data-testid="link-package-selector-empty">No matches</div>
          )}
          {!loading && !loadError && results.map((item, index) => {
            const key = item.id == null ? undefined : String(item.id);
            return (
              <button
                key={key ?? `missing-package-${index}`}
                type="button"
                onMouseDown={() => handleSelect(item)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  background: 'transparent',
                  padding: '6px 8px',
                  borderRadius: 6,
                  cursor: 'pointer'
                }}
                aria-label={`Select ${getResultLabel(item)}`}
                data-testid={key ? `link-package-selector-option-${key}` : undefined}
              >
                <div style={{ fontWeight: 700, fontSize: 13 }}>{getResultLabel(item)}</div>
                {item?.label && (
                  <div className="muted" style={{ fontSize: 11 }}>{item.label}</div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  )
}

export default React.memo(LinkPackageSelector);
